import { Component, DestroyRef, HostListener, Injector, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar } from '@capacitor/status-bar';
import { Network } from '@capacitor/network';
import type { ConnectionStatus } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { Observable, catchError, firstValueFrom, of, timeout } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastController, IonApp, IonRouterOutlet } from '@ionic/angular';
import { onAuthStateChanged } from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { FIREBASE_AUTH } from './firebase-config';
import { Title } from '@angular/platform-browser';
import { getRuntimePwaAuthConfig } from './utils/runtime-config';

// MODELS
import { BebopUser } from './interfaces/user';

// SERVICES
import { GlobalService } from './services/global.service';
import { AuthenticationService } from './services/authentication.service';
import { UserService } from './services/user.service';
import { SecureStorageService } from './services/secure-storage.service';
import { PushNotificationsService } from './services/push-notifications.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [IonApp, IonRouterOutlet]
})
export class AppComponent {
  public router = inject(Router);
  public translate = inject(TranslateService);
  private platform = inject(Platform);
  public globalService = inject(GlobalService);
  public authService = inject(AuthenticationService);
  public toastCtrl = inject(ToastController);
  public userService = inject(UserService);
  private auth = inject(FIREBASE_AUTH);
  private titleService = inject(Title);
  private storage = inject(SecureStorageService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private pushNotificationsService = inject(PushNotificationsService);

  public pwaAuthChecked = signal(Capacitor.getPlatform() !== 'web');
  public showCloudLogin = signal(Capacitor.getPlatform() === 'web');
  public cloudLoginMessage = signal('');
  public status: ConnectionStatus | null = null;
  public listener: Promise<PluginListenerHandle> | null = null;
  public isAppStateChangeListenerAdded = false;
  private toast: HTMLIonToastElement | null = null;
  private splashHidden = false;
  private pushNotificationsInitialized = false;

  public user$!: Observable<BebopUser | undefined>;

  constructor() {
    // this.router.navigate(['/public/home']);
    void this.checkForPWA();
    this.initializeApp();
    // Bind tap/receive listeners immediately so a cold-start notification tap isn't lost
    // or delayed behind auth/profile loading (see initializePushNotifications for the
    // permission/token registration, which does need an authenticated user).
    this.pushNotificationsService.bindNativeListenersEarly().catch(error => {
      console.warn('Push notification early listener bind failed:', error);
    });
    this.setupGlobalFocusBlur();
    this.setupPublicLanguageLinkSync();
  }

  @HostListener('document:click', ['$event'])
  async onPublicLanguageLinkClick(event: MouseEvent): Promise<void> {
    const target = event.target as Element | null;
    const languageTrigger = target?.closest('.public-english-link');

    if (!languageTrigger) {
      return;
    }

    event.preventDefault();
    const currentLanguage = this.resolveCurrentLanguage();
    const nextLanguage = currentLanguage === 'en' ? 'nl' : 'en';
    await this.setPublicLanguage(nextLanguage);
  }

  // Remove focus on navigation to prevent aria-hidden warnings
  private setupGlobalFocusBlur() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      });
  }

  private setupPublicLanguageLinkSync(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updatePublicLanguageLinks();
      });

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        // Run after route view renders so newly-created header links are updated.
        setTimeout(() => this.updatePublicLanguageLinks(), 0);
      });
  }

  private updatePublicLanguageLinks(): void {
    const currentLanguage = this.resolveCurrentLanguage();
    const label = currentLanguage === 'en' ? 'Nederlands' : 'English';
    const links = document.querySelectorAll<HTMLAnchorElement>('a.public-english-link');

    links.forEach((link) => {
      link.textContent = label;
      link.setAttribute('data-public-language', currentLanguage === 'en' ? 'nl' : 'en');
    });
  }

  initializeApp() {
    this.platform.ready().then(() => {
      this.setStatusBar();
      this.translate.setFallbackLang('nl');
      this.setUserLanguage();
      this.getNetStatus();
      this.listenToNetwork();
      // Initialize Firebase auth state listener
      this.getUser();
      this.watchMaintenanceMode();
      this.setTheme();
      this.titleService.setTitle(environment.name);
    });
  }

  private resolveCurrentLanguage(): string {
    return this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'nl';
  }

  private watchMaintenanceMode(): void {
    this.globalService
      .getMaintenanceMode()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((maintenanceEnabled) => {
        if (!maintenanceEnabled) {
          return;
        }

        if (this.router.url.startsWith('/public/maintenance')) {
          return;
        }

        void this.router.navigateByUrl('/public/maintenance', {
          replaceUrl: true,
        });
      });
  }

  async setUserLanguage() {
    try {
      const currentUser = this.auth.currentUser;
      if (currentUser?.uid) {
        const serverProfile = await this.userService.getUserProfileFromServer();
        if (serverProfile?.setting_language === 'en' || serverProfile?.setting_language === 'nl') {
          this.translate.use(serverProfile.setting_language);
          this.updatePublicLanguageLinks();
          return;
        }

        const profile = await firstValueFrom(
          this.userService.getUserProfile().pipe(
            timeout(5000),
            catchError(() => of(undefined))
          )
        );
        if (profile?.setting_language === 'en' || profile?.setting_language === 'nl') {
          this.translate.use(profile.setting_language);
          this.updatePublicLanguageLinks();
          return;
        }

        const publicSettings = await this.storage.getObject<{ language?: string }>('public_settings');
        const currentLanguage = this.translate.getCurrentLang();
        const fallbackLanguage = this.translate.getFallbackLang();

        const resolvedLanguage: 'nl' | 'en' =
          publicSettings?.language === 'en' || publicSettings?.language === 'nl'
            ? publicSettings.language
            : currentLanguage === 'en' || currentLanguage === 'nl'
              ? currentLanguage
              : fallbackLanguage === 'en' || fallbackLanguage === 'nl'
                ? fallbackLanguage
                : 'nl';

        this.translate.use(resolvedLanguage);
        this.updatePublicLanguageLinks();
      } else {
        const publicSettings = await this.storage.getObject<{ language?: string }>('public_settings');
        this.translate.use(publicSettings?.language || 'nl');
        this.updatePublicLanguageLinks();
      }
    } catch (error) {
      console.error('Error setting user language:', error);
      this.translate.use('nl');
      this.updatePublicLanguageLinks();
    }
  }

  async hideSplashScreen() {
    if (!this.splashHidden) {
      this.splashHidden = true;
      try {
        await SplashScreen.hide();
      } catch (err) {
        console.error('Error hiding splash screen:', err);
      }
    }
  }

  async checkForPWA() {
    if (Capacitor.getPlatform() === 'web') {
      try {
        const pwaAuth = await this.storage.getItem('pwa_auth');
        const runtimeAuthConfig = getRuntimePwaAuthConfig();
        const shouldRequireLogin = runtimeAuthConfig.enabled && !pwaAuth;
        this.showCloudLogin.set(Boolean(shouldRequireLogin));
      } finally {
        this.pwaAuthChecked.set(true);
      }
    }
  }

  async loginCloud(username: string, password: string) {
    const runtimeAuthConfig = getRuntimePwaAuthConfig();

    if (runtimeAuthConfig.enabled && username === runtimeAuthConfig.username && password === runtimeAuthConfig.password) {
      await this.storage.setItem('pwa_auth', 'true');
      this.showCloudLogin.set(false);
      this.cloudLoginMessage.set('');
    } else {
      await this.storage.removeItem('pwa_auth');
      this.cloudLoginMessage.set('Invalid username or password. Please try again.');
      this.authService.logPwaLoginAttempt(username);
    }
  }

  async setStatusBar() {
    if (this.platform.is('android') && Capacitor.isPluginAvailable('StatusBar')) {
      await StatusBar.setOverlaysWebView({ overlay: false });  
      await StatusBar.setBackgroundColor({ color: '#ff4500' });
    }
  }

  async setStatusbarColor(color: string) {
    if (this.platform.is('android') && Capacitor.isPluginAvailable('StatusBar')) {
      await StatusBar.setBackgroundColor({ color: color });
    }
  }

  getUser() {
    // Create an observable from onAuthStateChanged callback
    const authUnsubscribe = onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        void this.setUserLanguage();

        if (!firebaseUser.emailVerified) {
          this.hideSplashScreen(); // Hide splash screen even if email not verified
        } else {
          this.user$ = this.userService.getUserProfile();
          this.user$.pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe(userProfile => {
            // Hide splash screen as soon as we have user profile data
            this.hideSplashScreen();
            
            if (userProfile) {
              this.initializePushNotifications();
              this.appStateListener();
            }
          });
        }
      } else {
        this.pushNotificationsInitialized = false;
        void this.setUserLanguage();
        void this.setTheme();
        // Hide splash screen even if not logged in
        this.hideSplashScreen();
      }
    });
  }

  async appStateListener() {
    if (!this.isAppStateChangeListenerAdded) {
      this.isAppStateChangeListenerAdded = true;
      this.userService.saveAppState('foreground');

      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          this.userService.saveAppState('background');
        } else {
          this.userService.saveAppState('foreground');
        }
      });
    }
  }

  private initializePushNotifications(): void {
    if (this.pushNotificationsInitialized) {
      return;
    }
    this.pushNotificationsInitialized = true;
    this.pushNotificationsService.initializeNotifications().catch(error => {
      console.warn('Push notification init failed:', error);
    });
  }

  async getNetStatus() {
    try {
      this.status = await Network.getStatus();
      if (!this.status.connected) {
        this.presentNoNetwerkToast();
      } else {
        this.dismissNoNetwerkToast();
      }
    } catch (e) { console.log('Error getting NetStatus', e) }
  }

  listenToNetwork() {
    this.listener = Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      if (!status.connected) {
        this.presentNoNetwerkToast();
      } else {
        this.dismissNoNetwerkToast();
      }
    });
  }

  async presentNoNetwerkToast() {
    this.globalService.setOffline(true);
    const toast = await this.toastCtrl.create({
      header: this.globalService.translateCode('shared.offline_popup.header'),
      message: this.globalService.translateCode('shared.offline_popup.message'),
      position: 'top',
      duration: 0,
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => {
            toast.dismiss();
          }
        }
      ]
    });
    this.toast = toast;
    await toast.present();
  }

  dismissNoNetwerkToast() {
    this.globalService.setOffline(false);
    this.toast?.dismiss();
  }

  async setTheme() {
    const currentUser = this.auth.currentUser;
    if (currentUser?.uid) {
      return;
    }

    const publicSettings = await this.storage.getObject<{ darkmode?: boolean }>('public_settings');
    const useDarkMode = typeof publicSettings?.darkmode === 'boolean'
      ? publicSettings.darkmode
      : false;

    if (useDarkMode) {
      document.documentElement.classList.add('ion-palette-dark');
    } else {
      document.documentElement.classList.remove('ion-palette-dark');
    }
  }

  private async setPublicLanguage(language: string): Promise<void> {
    this.translate.use(language);

    const currentSettings = await this.storage.getObject<{ darkmode?: boolean; language?: string }>('public_settings');
    await this.storage.setObject('public_settings', {
      ...(currentSettings || {}),
      language,
    });

    this.updatePublicLanguageLinks();
  }
}
